'use client';

import { c as _c } from "react/compiler-runtime";
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
type BannerVisualProps = {
  startTime?: string;
  endTime?: string;
  title?: string;
  message?: string;
  dismissAction: () => void;
  className?: string;
};
function FormattedDate(t0) {
  const $ = _c(8);
  const {
    date
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== date) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "UTC"
    });
    t1 = date.toISOString();
    t2 = true;
    t3 = formatter.format(date);
    $[0] = date;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <time dateTime={t1} suppressHydrationWarning={t2}>{t3} UTC</time>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function TimeDisplay(t0) {
  const $ = _c(12);
  const {
    startDate,
    endDate,
    isInProgress
  } = t0;
  const hasStart = startDate && !isInProgress;
  const hasEnd = endDate;
  if (!hasStart && !hasEnd) {
    return null;
  }
  if (hasStart && hasEnd) {
    let t1;
    if ($[0] !== startDate) {
      t1 = <span className="rounded-full bg-white/10 px-2 py-0.5 font-mono"><FormattedDate date={startDate} /></span>;
      $[0] = startDate;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span>–</span>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== endDate) {
      t3 = <span className="rounded-full bg-white/10 px-2 py-0.5 font-mono"><FormattedDate date={endDate} /></span>;
      $[3] = endDate;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t1 || $[6] !== t3) {
      t4 = <span className="inline-flex items-center gap-1.5 text-xs opacity-90">{t1}{t2}{t3}</span>;
      $[5] = t1;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (hasEnd) {
    let t1;
    if ($[8] !== endDate) {
      t1 = <span className="inline-flex items-center gap-1.5 text-xs opacity-90">Ends{" "}<span className="rounded-full bg-white/10 px-2 py-0.5 font-mono"><FormattedDate date={endDate} /></span></span>;
      $[8] = endDate;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    return t1;
  }
  if (hasStart) {
    let t1;
    if ($[10] !== startDate) {
      t1 = <span className="inline-flex items-center gap-1.5 text-xs opacity-90">Starts{" "}<span className="rounded-full bg-white/10 px-2 py-0.5 font-mono"><FormattedDate date={startDate} /></span></span>;
      $[10] = startDate;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    return t1;
  }
  return null;
}
export function BannerVisual(t0) {
  const $ = _c(28);
  const {
    startTime,
    endTime,
    title,
    message,
    dismissAction,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const now = t1;
  let t2;
  if ($[1] !== startTime) {
    t2 = startTime ? new Date(startTime) : null;
    $[1] = startTime;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const startDate = t2;
  let t3;
  if ($[3] !== endTime) {
    t3 = endTime ? new Date(endTime) : null;
    $[3] = endTime;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const endDate = t3;
  const isInProgress = startDate && now >= startDate;
  const displayTitle = title || (isInProgress ? "System Maintenance" : "Scheduled Maintenance");
  const displayMessage = message || (isInProgress ? "We are currently upgrading our services." : "We will be performing system updates.");
  let t4;
  if ($[5] !== className) {
    t4 = cn("relative w-full bg-christ-black-900 px-4 py-3 text-christ-white-900", className);
    $[5] = className;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== displayTitle) {
    t5 = <span className="font-semibold">{displayTitle}</span>;
    $[7] = displayTitle;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== displayMessage) {
    t6 = <span className="opacity-90">{displayMessage}</span>;
    $[9] = displayMessage;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== endDate || $[12] !== isInProgress || $[13] !== startDate) {
    t7 = <TimeDisplay startDate={startDate} endDate={endDate} isInProgress={isInProgress} />;
    $[11] = endDate;
    $[12] = isInProgress;
    $[13] = startDate;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">{t5}{t6}{t7}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <button type="submit" className="group p-1"><X className="h-4 w-4 opacity-70 transition-opacity group-hover:opacity-100" /><span className="sr-only">Dismiss</span></button>;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== dismissAction) {
    t10 = <form action={dismissAction} className="shrink-0">{t9}</form>;
    $[20] = dismissAction;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t4) {
    t12 = <div className={t4}>{t11}</div>;
    $[25] = t11;
    $[26] = t4;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}